import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'

/**
 * WikiRecentChanges - Modal showing recent auto-applied wiki changes with undo capability
 * 
 * Shows changes from the last 24 hours that were auto-applied by the AI system.
 * Allows team managers to undo any changes they don't want.
 */
function WikiRecentChanges({ teamId, onClose, onUndo }) {
  const [changes, setChanges] = useState([])
  const [loading, setLoading] = useState(true)
  const [undoing, setUndoing] = useState({})

  useEffect(() => {
    if (teamId) {
      fetchRecentChanges()
    }
  }, [teamId])

  const fetchRecentChanges = async () => {
    setLoading(true)
    try {
      // Get changes from the last 7 days that haven't been undone
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      
      const { data, error } = await supabase
        .from('wiki_change_log')
        .select(`
          id,
          operation,
          new_data,
          previous_data,
          confidence_score,
          reasoning,
          created_at,
          wiki_item_id,
          dispatch_id,
          dispatches(title)
        `)
        .eq('team_id', teamId)
        .eq('auto_applied', true)
        .is('undone_at', null)
        .gte('created_at', sevenDaysAgo)
        .order('created_at', { ascending: false })

      if (error) throw error
      setChanges(data || [])
    } catch (err) {
      console.error('Failed to fetch recent changes:', err)
      toast.error('Failed to load recent changes')
    } finally {
      setLoading(false)
    }
  }

  const handleUndo = async (changeId, forceUndo = false) => {
    const change = changes.find(c => c.id === changeId)
    if (!change) return

    if (!forceUndo) {
      const confirmed = await confirmDialog({
        title: 'Undo this change?',
        message: `This will ${change.operation === 'create' ? 'remove' : 'revert'} "${change.new_data?.title || 'this item'}".`,
        confirmText: 'Undo',
        confirmVariant: 'danger'
      })

      if (!confirmed) return
    }

    setUndoing(prev => ({ ...prev, [changeId]: true }))

    try {
      // Note: undo_wiki_change RPC uses auth.uid() internally, no need to pass user_id
      const { data, error } = await supabase.rpc('undo_wiki_change', {
        p_change_id: changeId,
        p_team_id: teamId,
        p_force: forceUndo
      })

      if (error) throw error

      if (data?.success) {
        if (data.was_force_undo) {
          toast.success('Change force-undone (your edits were overwritten)')
        } else {
          toast.success('Change undone successfully')
        }
        // Remove from local state
        setChanges(prev => prev.filter(c => c.id !== changeId))
        onUndo?.()
      } else if (data?.modified_since) {
        // Item was modified since the AI change - ask for confirmation
        const forceConfirmed = await confirmDialog({
          title: 'Item was modified!',
          message: `This item was edited after the AI updated it. Undoing will overwrite your recent changes.\n\nCurrent: "${data.current_title}"\n\nProceed anyway?`,
          confirmText: 'Force Undo',
          confirmVariant: 'danger'
        })
        
        if (forceConfirmed) {
          // Retry with force flag
          handleUndo(changeId, true)
        }
      } else {
        toast.error(data?.error || 'Failed to undo change')
      }
    } catch (err) {
      console.error('Failed to undo change:', err)
      toast.error('Failed to undo change')
    } finally {
      setUndoing(prev => ({ ...prev, [changeId]: false }))
    }
  }

  const handleUndoAll = async () => {
    if (changes.length === 0) return

    const confirmed = await confirmDialog({
      title: 'Undo all changes?',
      message: `This will undo ${changes.length} auto-applied ${changes.length === 1 ? 'change' : 'changes'}.`,
      confirmText: 'Undo All',
      confirmVariant: 'danger'
    })

    if (!confirmed) return

    let successCount = 0

    for (const change of changes) {
      try {
        // Note: undo_wiki_change RPC uses auth.uid() internally, no need to pass user_id
        // For "Undo All", we skip modified items unless forced
        const { data } = await supabase.rpc('undo_wiki_change', {
          p_change_id: change.id,
          p_team_id: teamId,
          p_force: false
        })
        if (data?.success) successCount++
        // Silently skip modified items in bulk undo
      } catch (err) {
        console.error('Failed to undo change:', change.id, err)
      }
    }

    toast.success(`Undone ${successCount} of ${changes.length} changes`)
    setChanges([])
    onUndo?.()
  }

  const getOperationLabel = (operation) => {
    switch (operation) {
      case 'create': return 'Added'
      case 'update': return 'Updated'
      case 'delete': return 'Removed'
      default: return operation
    }
  }

  const getOperationBadgeVariant = (operation) => {
    switch (operation) {
      case 'create': return 'success'
      case 'update': return 'info'
      case 'delete': return 'danger'
      default: return 'secondary'
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    return `${diffDays} days ago`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div>
            <h2 className="font-heading text-xl font-bold">Recent Wiki Updates</h2>
            <p className="text-sm text-brand-slate mt-1">
              Auto-applied changes from the last 7 days
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-brand-slate hover:text-brand-charcoal text-xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12 text-brand-slate">
              Loading changes...
            </div>
          ) : changes.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-brand-slate">No recent auto-updates</p>
              <p className="text-sm text-brand-slate mt-1">
                Changes will appear here when AI automatically updates your wiki
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {changes.map(change => (
                <div
                  key={change.id}
                  className="p-4 border border-border rounded-lg bg-white"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant={getOperationBadgeVariant(change.operation)}>
                          {getOperationLabel(change.operation)}
                        </Badge>
                        <span className="text-xs text-brand-slate">
                          {formatDate(change.created_at)}
                        </span>
                        {change.confidence_score && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {Math.round(change.confidence_score * 100)}% confidence
                          </span>
                        )}
                      </div>
                      
                      <h4 className="font-medium text-brand-charcoal mb-1">
                        {change.new_data?.title || 'Untitled'}
                      </h4>
                      
                      {change.new_data?.content && (
                        <p className="text-sm text-brand-slate line-clamp-2">
                          {change.new_data.content.replace(/<[^>]*>/g, '').substring(0, 150)}
                          {change.new_data.content.length > 150 && '...'}
                        </p>
                      )}

                      {change.reasoning && (
                        <p className="text-xs text-blue-600 mt-2">
                          💡 {change.reasoning}
                        </p>
                      )}

                      {change.dispatches?.title && (
                        <p className="text-xs text-brand-slate mt-2">
                          From dispatch: "{change.dispatches.title}"
                        </p>
                      )}
                    </div>

                    <Button
                      variant="secondary"
                      onClick={() => handleUndo(change.id)}
                      disabled={undoing[change.id]}
                      className="text-sm text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0"
                    >
                      {undoing[change.id] ? 'Undoing...' : 'Undo'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
          <div className="text-sm text-brand-slate">
            {changes.length > 0 && (
              <span>{changes.length} {changes.length === 1 ? 'change' : 'changes'}</span>
            )}
          </div>
          <div className="flex gap-2">
            {changes.length > 1 && (
              <Button
                variant="secondary"
                onClick={handleUndoAll}
                className="text-sm text-red-600"
              >
                Undo All
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default WikiRecentChanges
